import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import TestCard from './tests/TestCard';
import CreateTestModal from './tests/CreateTestModal';
import ProvideCookiesModal from './tests/ProvideCookiesModal';
import TestExecutionModal from './tests/TestExecutionModal';
import { Test } from '../types/test';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import CircularLoader from './CircularLoader';

const TestsDashboard: React.FC = () => {
  const [tests, setTests] = useState<Test[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [cookiesModal, setCookiesModal] = useState<{ isOpen: boolean; testId: string | null }>({
    isOpen: false,
    testId: null
  });
  const [loading, setLoading] = useState(true);
  const [testExecution, setTestExecution] = useState<{
    isOpen: boolean;
    test: Test | null;
    messages: Array<{ content: string; type: 'user' | 'assistant' | 'system' }>;
    toolCalls: Array<{ 
      id: string; 
      function_name: string; 
      timestamp: Date; 
      status: 'pending' | 'completed';
      call_id?: string;
    }>;
    isRunning: boolean;
  }>({
    isOpen: false,
    test: null,
    messages: [],
    toolCalls: [],
    isRunning: false
  });
  
  const { session, user } = useAuth();
  const wsConnectionRef = useRef<WebSocket | null>(null);
  const autoResponseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasReceivedAgentTextRef = useRef<boolean>(false);
  const currentResponseRef = useRef<string>('');
  const justAutoRespondedRef = useRef<boolean>(false);
  const dynamicRouteAttemptsRef = useRef<number>(0);
  const createdRouteNameRef = useRef<string | null>(null);
  const testExecutionRef = useRef(testExecution);
  const currentTestRef = useRef<Test | null>(null);

  // Update the refs whenever testExecution changes
  useEffect(() => {
    testExecutionRef.current = testExecution;
    currentTestRef.current = testExecution.test;
  }, [testExecution]);

  useEffect(() => {
    fetchTests();
    
    // Set up polling to refresh test status
    const interval = setInterval(() => {
      fetchTests();
    }, 5000); // Refresh every 5 seconds
    
    // Initialize WebSocket connection
    if (session?.access_token) {
      connectWebSocket();
    }
    
    return () => {
      clearInterval(interval);
      if (wsConnectionRef.current) {
        wsConnectionRef.current.close();
      }
    };
  }, [session?.access_token]);

  const fetchTests = async () => {
    try {
      console.log('Fetching tests...', { session: !!session, token: !!session?.access_token });
      const response = await axios.get('/api/admin/tests', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      console.log('Tests response:', response.data);
      setTests(response.data.tests || []);
    } catch (error) {
      console.error('Error fetching tests:', error);
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRunTest = async (testId: string, cookies?: string) => {
    try {
      await axios.post('/api/admin/tests/run', 
        { test_id: testId, cookies },
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      fetchTests();
    } catch (error) {
      console.error('Error running test:', error);
    }
  };

  const handleRunAll = async () => {
    try {
      await axios.post('/api/admin/tests/run-all', {}, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      fetchTests();
    } catch (error) {
      console.error('Error running all tests:', error);
    }
  };

  const handleCreateTest = async (harFile: File, sessionId: string, task: string) => {
    const formData = new FormData();
    formData.append('har_file', harFile);

    try {
      console.log('Creating test...', { sessionId, task, fileName: harFile.name });
      const response = await axios.post(
        `/api/admin/tests/create?context_id=${encodeURIComponent(sessionId)}&task=${encodeURIComponent(task)}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      console.log('Test created:', response.data);
      await fetchTests(); // Wait for fetch to complete
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating test:', error);
      alert('Failed to create test. Check console for details.');
    }
  };

  const handleDeleteTest = async (testId: string) => {
    try {
      console.log('Deleting test...', testId);
      await axios.delete(`/api/admin/tests/${testId}`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      console.log('Test deleted successfully');
      await fetchTests(); // Refresh the list
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Failed to delete test. Check console for details.');
    }
  };

  const updateCurrentResponse = () => {
    setTestExecution(prev => {
      const newMessages = [...prev.messages];
      const lastMessage = newMessages[newMessages.length - 1];
      
      if (lastMessage && lastMessage.type === 'assistant') {
        // Update existing assistant message
        lastMessage.content = currentResponseRef.current;
      } else {
        // Create new assistant message
        newMessages.push({
          content: currentResponseRef.current,
          type: 'assistant'
        });
      }
      
      return { ...prev, messages: newMessages };
    });
  };

  const markTestAsFailed = async () => {
    const currentTest = testExecution.test;
    if (!currentTest) return;
    
    try {
      // Call the API to update test status
      await axios.post('/api/admin/tests/run', 
        { 
          test_id: currentTest.id, 
          status: 'failed',
          test_result: 'fail'
        },
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      
      // Refresh tests to show updated status
      fetchTests();
    } catch (error) {
      console.error('Error updating test status:', error);
    }
  };

  const completeAgentExecution = async () => {
    console.log('Agent execution complete, executing route with session cookies...');
    const currentTest = currentTestRef.current;
    
    console.log('Current test:', currentTest);
    console.log('Created route name:', createdRouteNameRef.current);
    
    setTestExecution(prev => ({
      ...prev,
      messages: [...prev.messages, { 
        content: 'Route created successfully! Fetching cookies from session and executing...', 
        type: 'system' 
      }]
    }));
    
    // If we have a route name, execute the route with session cookies
    if (createdRouteNameRef.current && currentTest) {
      console.log('Starting route execution process...');
      try {
        // Add a small delay to ensure DynamoDB write is complete
        console.log('Waiting for route to be fully saved in DynamoDB...');
        await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
        
        // Use the route execution service endpoint
        setTestExecution(prev => ({
          ...prev,
          messages: [...prev.messages, { 
            content: `Executing route with authenticated cookies from session...`, 
            type: 'system' 
          }]
        }));
        
        // Extract just the route display name (remove user ID prefix if present)
        let routeDisplayName = createdRouteNameRef.current;
        if (routeDisplayName.includes(':')) {
          routeDisplayName = routeDisplayName.split(':')[1];
        }
        
        console.log('Executing route via admin endpoint:', routeDisplayName, 'with session:', currentTest.session_id);
        
        const executeResponse = await axios.post(
          '/api/admin/routes/execute',
          {
            route_name: routeDisplayName,
            context_id: currentTest.session_id
          },
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
            },
          }
        );
        
        let testResult = 'fail';
        let finalMessage = '';
        
        console.log('Route execution response:', executeResponse.data);
        
        // Check the execution result
        if (executeResponse.data.status === 'success' && executeResponse.data.response) {
          const routeResponse = executeResponse.data.response;
          
          // Check if the route response is 200 and has content
          const hasValidResponse = routeResponse.status_code === 200 && 
                                  routeResponse.body && 
                                  (typeof routeResponse.body === 'object' ? Object.keys(routeResponse.body).length > 0 : routeResponse.body.toString().trim().length > 0);
          
          if (hasValidResponse) {
            testResult = 'pass';
            finalMessage = `✅ Test PASSED: Route executed successfully with 200 status and valid response\n\nResponse:\n\`\`\`json\n${JSON.stringify(routeResponse.body, null, 2)}\n\`\`\``;
          } else {
            finalMessage = `❌ Test FAILED: Route returned status ${routeResponse.status_code || 'unknown'} or empty response`;
          }
        } else {
          // Handle error case - extract the actual error message
          const errorMessage = executeResponse.data.error || executeResponse.data.detail || 'Unknown error';
          finalMessage = `❌ Test FAILED: Route execution error - ${errorMessage}`;
        }
        
        // Update test status in DynamoDB based on execution results
        await axios.post('/api/admin/tests/run', 
          { 
            test_id: currentTest.id, 
            status: 'completed',
            test_result: testResult
          },
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
            },
          }
        );
        
        // Show final message
        setTestExecution(prev => ({
          ...prev,
          messages: [...prev.messages, { 
            content: finalMessage, 
            type: 'system' 
          }],
          isRunning: false
        }));
        
        // Close modal after 3 seconds and refresh tests
        setTimeout(() => {
          setTestExecution({ isOpen: false, test: null, messages: [], toolCalls: [], isRunning: false });
          fetchTests(); // Refresh to show updated test status
        }, 3000);
        
      } catch (error) {
        console.error('Error executing route:', error);
        let errorMessage = 'Error executing route: ';
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as any;
          console.log('Route execution error response:', axiosError.response?.data);
          if (axiosError.response?.data?.detail) {
            errorMessage += axiosError.response.data.detail;
          } else if (axiosError.response?.data) {
            errorMessage += JSON.stringify(axiosError.response.data);
          } else {
            errorMessage += error instanceof Error ? error.message : String(error);
          }
        } else {
          errorMessage += error instanceof Error ? error.message : String(error);
        }
        
        // Mark test as failed
        try {
          await axios.post('/api/admin/tests/run', 
            { 
              test_id: currentTest.id, 
              status: 'completed',
              test_result: 'fail'
            },
            {
              headers: {
                Authorization: `Bearer ${session?.access_token}`,
              },
            }
          );
          
          setTestExecution(prev => ({
            ...prev,
            messages: [...prev.messages, { 
              content: `❌ Test FAILED: ${errorMessage}`, 
              type: 'system' 
            }],
            isRunning: false
          }));
          
          // Close modal after 3 seconds and refresh tests
          setTimeout(() => {
            setTestExecution({ isOpen: false, test: null, messages: [], toolCalls: [], isRunning: false });
            fetchTests(); // Refresh to show updated test status
          }, 3000);
          
        } catch (updateError) {
          console.error('Error updating test status after execution error:', updateError);
          setTestExecution(prev => ({
            ...prev,
            messages: [...prev.messages, { 
              content: `❌ ${errorMessage}`, 
              type: 'system' 
            }],
            isRunning: false
          }));
        }
      }
    } else {
      setTestExecution(prev => ({
        ...prev,
        isRunning: false
      }));
    }
  };

  const connectWebSocket = () => {
    if (!session?.access_token) {
      console.error('Cannot connect WebSocket - no authentication token');
      return;
    }

    if (wsConnectionRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    const wsUrl = `ws://localhost:8000/ws?token=${session.access_token}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected for test execution');
      wsConnectionRef.current = ws;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Test WebSocket message:', data);
        console.log('Message type:', data.type, 'Function name:', data.function_name);
        
        switch (data.type) {
          case 'agent_text':
            // Handle regular text content from the agent
            currentResponseRef.current += data.content;
            updateCurrentResponse();
            
            // Mark that we received agent text
            hasReceivedAgentTextRef.current = true;
            
            // Clear any existing timeout
            if (autoResponseTimeoutRef.current) {
              clearTimeout(autoResponseTimeoutRef.current);
            }
            
            // Set a new timeout to auto-respond after receiving agent_text
            console.log('Setting auto-response timeout for agent_text');
            autoResponseTimeoutRef.current = setTimeout(() => {
              console.log('Auto-response timeout fired, checking conditions...');
              console.log('hasReceivedAgentTextRef.current:', hasReceivedAgentTextRef.current);
              console.log('wsConnectionRef.current ready:', wsConnectionRef.current?.readyState === WebSocket.OPEN);
              
              if (hasReceivedAgentTextRef.current && wsConnectionRef.current?.readyState === WebSocket.OPEN) {
                // Check if the accumulated message contains JSON structure with input/output
                const currentTestExecution = testExecutionRef.current;
                const lastAssistantMessage = [...currentTestExecution.messages].reverse().find(m => m.type === 'assistant');
                const messageContent = lastAssistantMessage?.content || currentResponseRef.current;
                
                console.log('Checking message content for JSON structure:', messageContent.substring(0, 200));
                
                // Look for JSON blocks with input/output structure (be more flexible)
                const hasJsonBlock = messageContent.includes('```json') || messageContent.includes('```{');
                const hasInputOutput = (messageContent.includes('"input"') && messageContent.includes('"output"')) ||
                                     (messageContent.includes('"request"') && messageContent.includes('"response"')) ||
                                     (messageContent.includes('input') && messageContent.includes('output'));
                // Be flexible - if we have input/output patterns, that's often enough
                const hasJsonStructure = hasInputOutput;
                
                console.log('Has JSON block:', hasJsonBlock);
                console.log('Has input/output structure:', hasInputOutput);
                console.log('Has JSON structure:', hasJsonStructure);
                console.log('Agent text accumulated content preview:', messageContent.substring(0, 500));
                
                if (hasJsonStructure) {
                  console.log('Auto-responding with okay to JSON structure proposal');
                  setTestExecution(prev => ({
                    ...prev,
                    messages: [...prev.messages, { content: 'okay', type: 'user' }]
                  }));
                  
                  wsConnectionRef.current.send(JSON.stringify({
                    type: 'user_message',
                    content: 'okay',
                    include_js_call_frames: true
                  }));
                  
                  // Mark that we just auto-responded
                  justAutoRespondedRef.current = true;
                }
                
                // Reset the flag
                hasReceivedAgentTextRef.current = false;
              }
            }, 2000); // Wait 2 seconds after the last agent_text to ensure the message is complete
            break;
            
          case 'final_response':
            // Final response from AI
            if (data.content) {
              currentResponseRef.current = data.content;
              updateCurrentResponse();
              
              // Check if this final response contains JSON structure with input/output (use same logic as agent_text)
              const hasJsonBlock = data.content.includes('```json') || data.content.includes('```{');
              const hasInputOutput = (data.content.includes('"input"') && data.content.includes('"output"')) ||
                                   (data.content.includes('"request"') && data.content.includes('"response"')) ||
                                   (data.content.includes('input') && data.content.includes('output'));
              // Be flexible - if we have input/output patterns, that's often enough
              const hasJsonStructure = hasInputOutput;
              
              console.log('Final response has JSON block:', hasJsonBlock);
              console.log('Final response has input/output structure:', hasInputOutput);
              console.log('Final response has JSON structure:', hasJsonStructure);
              console.log('Final response content preview:', data.content.substring(0, 500));
              
              if (hasJsonStructure && wsConnectionRef.current?.readyState === WebSocket.OPEN) {
                // Clear any pending auto-response timeout since we're handling it now
                if (autoResponseTimeoutRef.current) {
                  console.log('Clearing pending auto-response timeout to handle final_response immediately');
                  clearTimeout(autoResponseTimeoutRef.current);
                }
                
                setTimeout(() => {
                  console.log('Auto-responding with okay to final_response JSON structure');
                  setTestExecution(prev => ({
                    ...prev,
                    messages: [...prev.messages, { content: 'okay', type: 'user' }]
                  }));
                  
                  if (wsConnectionRef.current) {
                    wsConnectionRef.current.send(JSON.stringify({
                      type: 'user_message',
                      content: 'okay',
                      include_js_call_frames: true
                    }));
                  }
                  
                  // Mark that we just auto-responded
                  justAutoRespondedRef.current = true;
                }, 1000); // Shorter delay for final_response
              } else {
                // If no JSON structure, let the pending agent_text timeout handle it
                console.log('Final response has no JSON structure, keeping pending auto-response timeout');
                
                // Add a fallback timeout in case the agent_text timeout was missed
                setTimeout(() => {
                  console.log('Fallback auto-response check triggered');
                  if (!justAutoRespondedRef.current && wsConnectionRef.current?.readyState === WebSocket.OPEN) {
                    // Check the current message content one more time
                    const currentTestExecution = testExecutionRef.current;
                    const lastAssistantMessage = [...currentTestExecution.messages].reverse().find(m => m.type === 'assistant');
                    const fallbackContent = lastAssistantMessage?.content || '';
                    
                    const fallbackHasJsonBlock = fallbackContent.includes('```json');
                    const fallbackHasInputOutput = (fallbackContent.includes('"input"') && fallbackContent.includes('"output"')) ||
                                                  (fallbackContent.includes('"request"') && fallbackContent.includes('"response"')) ||
                                                  (fallbackContent.includes('input') && fallbackContent.includes('output'));
                    
                    console.log('Fallback check - has JSON block:', fallbackHasJsonBlock);
                    console.log('Fallback check - has input/output:', fallbackHasInputOutput);
                    
                    // Be more flexible - if we have input/output structure, that's enough
                    // The agent might not always use ```json``` code blocks
                    if (fallbackHasInputOutput) {
                      console.log('Fallback auto-responding with okay (found input/output structure)');
                      setTestExecution(prev => ({
                        ...prev,
                        messages: [...prev.messages, { content: 'okay', type: 'user' }]
                      }));
                      
                      wsConnectionRef.current.send(JSON.stringify({
                        type: 'user_message',
                        content: 'okay',
                        include_js_call_frames: true
                      }));
                      
                      justAutoRespondedRef.current = true;
                    } else {
                      console.log('Fallback check - no auto-response needed');
                    }
                  }
                }, 3000); // 3 second fallback delay
              }
            }
            
            // Clear the current response ref after a delay
            setTimeout(() => {
              currentResponseRef.current = '';
            }, 100);
            
            // Don't mark as complete here - wait for 'complete' message
            // Just reset the agent text flag
            hasReceivedAgentTextRef.current = false;
            break;
            
          case 'complete':
            console.log('Received complete message');
            console.log('Dynamic route attempts so far:', dynamicRouteAttemptsRef.current);
            console.log('Created route name:', createdRouteNameRef.current);
            
            // If we have successfully created a route, ignore this complete message
            if (createdRouteNameRef.current) {
              console.log('Ignoring complete message - route was already created successfully');
              break;
            }
            
            // If we haven't attempted make_dynamic_route yet, don't complete
            if (dynamicRouteAttemptsRef.current === 0) {
              console.log('Ignoring complete message - no make_dynamic_route attempts yet, conversation should continue');
              // Don't clear timeouts or mark as complete - let the conversation continue
              break;
            }
            
            // Clear any pending auto-response only if we're actually completing
            if (autoResponseTimeoutRef.current) {
              console.log('Clearing auto-response timeout due to complete message');
              clearTimeout(autoResponseTimeoutRef.current);
            }
            hasReceivedAgentTextRef.current = false;
            
            // If we've tried make_dynamic_route but haven't succeeded, mark as failed
            if (dynamicRouteAttemptsRef.current > 0 && !createdRouteNameRef.current) {
              console.log('Agent completed without successful make_dynamic_route - marking as failed');
              
              setTestExecution(prev => ({
                ...prev,
                messages: [...prev.messages, { 
                  content: '❌ Agent execution failed: Agent completed without creating a route', 
                  type: 'system' 
                }],
                isRunning: false
              }));
              
              // Update test status in DynamoDB
              markTestAsFailed();
            }
            break;
            
          case 'tool_call':
            // Add to tool calls tracking
            const toolCallId = data.call_id || `tool_${Date.now()}`;
            
            // Track make_dynamic_route attempts
            if (data.function_name === 'make_dynamic_route') {
              dynamicRouteAttemptsRef.current++;
              console.log(`make_dynamic_route attempt #${dynamicRouteAttemptsRef.current}`);
            }
            
            setTestExecution(prev => ({
              ...prev,
              toolCalls: [...prev.toolCalls, {
                id: toolCallId,
                function_name: data.function_name,
                timestamp: new Date(),
                status: 'pending',
                call_id: data.call_id
              }],
              messages: [...prev.messages, { 
                content: `🔧 Calling: ${data.function_name}`, 
                type: 'system' 
              }]
            }));
            break;
            
          case 'tool_response':
            // Update tool call status first
            setTestExecution(prev => ({
              ...prev,
              toolCalls: prev.toolCalls.map(call => 
                (call.call_id === data.call_id || call.function_name === data.function_name)
                  ? { ...call, status: 'completed' }
                  : call
              )
            }));
            
            // Check if this is a make_dynamic_route response
            if (data.function_name === 'make_dynamic_route') {
              console.log('make_dynamic_route response:', data.response);
              
              // Parse the response
              let responseData;
              try {
                responseData = typeof data.response === 'string' ? JSON.parse(data.response) : data.response;
              } catch (e) {
                responseData = data.response;
              }
              
              // Check if successful based on status field
              const isSuccess = responseData?.status === 'success';
              
              if (isSuccess) {
                // Extract route name from response
                try {
                  console.log('Parsed response data:', responseData);
                  
                  // Try multiple possible locations for the route name - prioritize ID (internal name) over display name
                  const routeName = responseData.route?.id ||           // Internal route ID (preferred)
                                   responseData.route_name || 
                                   responseData.route?.name || 
                                   responseData.route?.display_name ||
                                   responseData.name;
                  
                  if (routeName) {
                    createdRouteNameRef.current = routeName;
                    console.log('Extracted route name:', routeName);
                  } else {
                    console.log('Could not find route name in response');
                  }
                } catch (e) {
                  console.log('Could not parse route name from response:', e);
                }
                
                // Show success message
                setTestExecution(prev => ({
                  ...prev,
                  messages: [...prev.messages, { 
                    content: `✅ Successfully created route: ${createdRouteNameRef.current || 'unknown'}`, 
                    type: 'system' 
                  }]
                }));
                
                // Mark agent execution as complete - use setTimeout to ensure it runs even if WebSocket closes
                console.log('Calling completeAgentExecution after successful make_dynamic_route');
                setTimeout(() => {
                  completeAgentExecution();
                }, 100); // Small delay to ensure this runs even if WebSocket closes immediately
              } else {
                // Show error message
                const errorMessage = responseData?.error || responseData?.message || 'Unknown error';
                setTestExecution(prev => ({
                  ...prev,
                  messages: [...prev.messages, { 
                    content: `❌ Failed to create route: ${errorMessage}`, 
                    type: 'system' 
                  }]
                }));
                
                if (dynamicRouteAttemptsRef.current >= 3) {
                  // Failed 3 times, mark test as failed
                  console.log('Agent failed after 3 attempts, marking test as failed');
                  
                  setTestExecution(prev => ({
                    ...prev,
                    messages: [...prev.messages, { 
                      content: '❌ Agent execution failed: Could not create route after 3 attempts', 
                      type: 'system' 
                    }],
                    isRunning: false
                  }));
                  
                  // Update test status in DynamoDB
                  markTestAsFailed();
                }
              }
            } else {
              // For other tool responses, show generic completion
              setTestExecution(prev => ({
                ...prev,
                messages: [...prev.messages, { 
                  content: `✅ Completed: ${data.function_name}`, 
                  type: 'system' 
                }]
              }));
            }
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      wsConnectionRef.current = null;
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  const handleRunBasic = async (test: Test) => {
    try {
      console.log('Running basic test...', test);
      
      // Reset auto-response state
      hasReceivedAgentTextRef.current = false;
      currentResponseRef.current = '';
      justAutoRespondedRef.current = false;
      dynamicRouteAttemptsRef.current = 0;
      createdRouteNameRef.current = null;
      if (autoResponseTimeoutRef.current) {
        clearTimeout(autoResponseTimeoutRef.current);
      }
      
      // Open the test execution modal
      setTestExecution({
        isOpen: true,
        test: test,
        messages: [
          { content: `Loading action recording: ${test.name}`, type: 'system' },
          { content: `Task: ${test.task}`, type: 'user' }
        ],
        toolCalls: [],
        isRunning: true
      });

      // Prepare the action recording (this now handles both S3 download and HAR service initialization)
      const prepareResponse = await fetch(`/api/admin/tests/${test.id}/prepare-har`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
        credentials: 'include',
      });

      if (!prepareResponse.ok) {
        throw new Error('Failed to prepare action recording');
      }

      const prepareData = await prepareResponse.json();
      
      // Add system message about action recording loaded
      setTestExecution(prev => ({
        ...prev,
        messages: [...prev.messages, { content: `Action recording loaded successfully (${prepareData.request_count} requests). Analyzing...`, type: 'system' }]
      }));

      // Send the task message via WebSocket to trigger AI analysis (use the same format as main App.tsx)
      if (wsConnectionRef.current?.readyState === WebSocket.OPEN) {
        wsConnectionRef.current.send(JSON.stringify({
          type: 'user_message',
          content: test.task,
          include_js_call_frames: true
        }));
      } else {
        throw new Error('WebSocket not connected');
      }

    } catch (error) {
      console.error('Error running basic test:', error);
      setTestExecution(prev => ({
        ...prev,
        messages: [...prev.messages, { content: `Error: ${error}`, type: 'system' }],
        isRunning: false
      }));
    }
  };

  const handleRunAuto = async (test: Test) => {
    try {
      console.log('Running auto test with cookie replacement...', test);
      
      // Reset auto-response state
      hasReceivedAgentTextRef.current = false;
      currentResponseRef.current = '';
      justAutoRespondedRef.current = false;
      dynamicRouteAttemptsRef.current = 0;
      createdRouteNameRef.current = null;
      if (autoResponseTimeoutRef.current) {
        clearTimeout(autoResponseTimeoutRef.current);
      }
      
      // Open the test execution modal
      setTestExecution({
        isOpen: true,
        test: test,
        messages: [
          { content: `Loading action recording: ${test.name}`, type: 'system' },
          { content: `Fetching authenticated cookies from session...`, type: 'system' },
          { content: `Task: ${test.task}`, type: 'user' }
        ],
        toolCalls: [],
        isRunning: true
      });

      // First, prepare the basic action recording
      const prepareResponse = await fetch(`/api/admin/tests/${test.id}/prepare-har`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
        credentials: 'include',
      });

      if (!prepareResponse.ok) {
        throw new Error('Failed to prepare action recording');
      }

      const prepareData = await prepareResponse.json();
      
      setTestExecution(prev => ({
        ...prev,
        messages: [...prev.messages, { 
          content: `Action recording loaded (${prepareData.request_count} requests). Getting fresh cookies...`, 
          type: 'system' 
        }]
      }));

      // Get authenticated cookies from session using the existing endpoint
      const authResponse = await fetch(
        `/browser/context/${test.session_id}/auth?user_id=${user?.id}`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
          credentials: 'include',
          keepalive: true
        }
      );

      if (!authResponse.ok) {
        throw new Error(`Failed to fetch cookies: ${authResponse.status} ${authResponse.statusText}`);
      }

      const authData = await authResponse.json();
      const cookies = authData.cookies;

      setTestExecution(prev => ({
        ...prev,
        messages: [...prev.messages, { 
          content: `Fresh cookies retrieved. Replacing cookies in action recording and analyzing...`, 
          type: 'system' 
        }]
      }));

      // TODO: Replace cookies in the action recording on the frontend side
      // For now, we'll proceed with the original action recording and use fresh cookies during execution
      
      // Ensure WebSocket is connected
      if (wsConnectionRef.current?.readyState !== WebSocket.OPEN) {
        console.log('WebSocket not connected, attempting to reconnect...');
        connectWebSocket();
        
        // Wait a bit for connection to establish
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check again
        if (wsConnectionRef.current?.readyState !== WebSocket.OPEN) {
          throw new Error('Failed to establish WebSocket connection');
        }
      }
      
      // Send the task message via WebSocket to trigger AI analysis
      wsConnectionRef.current.send(JSON.stringify({
        type: 'user_message',
        content: test.task,
        include_js_call_frames: true
      }));

    } catch (error) {
      console.error('Error running auto test:', error);
      setTestExecution(prev => ({
        ...prev,
        messages: [...prev.messages, { content: `Error: ${error}`, type: 'system' }],
        isRunning: false
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-950">
        <CircularLoader size="large" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-zinc-200 mb-2">Test Management</h2>
        <p className="text-zinc-400">Create and manage action recording tests</p>
      </div>

      <div className="space-y-6">
        {tests.length === 0 ? (
          <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-lg p-12 text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-zinc-300 mb-2">No tests yet</h3>
            <p className="text-zinc-500 mb-6">Create your first test to get started</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {tests.map((test) => (
              <TestCard
                key={test.id}
                test={test}
                onRunBasic={() => handleRunBasic(test)}
                onRunAuto={() => handleRunAuto(test)}
                onRunWithCookies={() => setCookiesModal({ isOpen: true, testId: test.id })}
                onDelete={() => handleDeleteTest(test.id)}
              />
            ))}
          </div>
        )}

        <div className="flex gap-4 justify-center mt-8">
          {tests.length > 0 && (
            <button
              onClick={handleRunAll}
              className="bg-zinc-800 hover:bg-zinc-700 px-6 py-3 rounded-lg font-medium text-zinc-200 transition-all duration-200 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              RUN ALL
            </button>
          )}
          
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            CREATE NEW TEST
          </button>
        </div>
      </div>

      <CreateTestModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateTest}
      />

      <ProvideCookiesModal
        isOpen={cookiesModal.isOpen}
        onClose={() => setCookiesModal({ isOpen: false, testId: null })}
        onSubmit={(cookies) => {
          if (cookiesModal.testId) {
            handleRunTest(cookiesModal.testId, cookies);
            setCookiesModal({ isOpen: false, testId: null });
          }
        }}
      />

      <TestExecutionModal
        isOpen={testExecution.isOpen}
        test={testExecution.test}
        messages={testExecution.messages}
        toolCalls={testExecution.toolCalls}
        isRunning={testExecution.isRunning}
        onClose={() => setTestExecution({ isOpen: false, test: null, messages: [], toolCalls: [], isRunning: false })}
      />
    </div>
  );
};

export default TestsDashboard;